import { Component, ChangeDetectionStrategy, inject, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { AspectRatio } from '@knorr-bremse-portals/ngx-components';
import { toPlainLink } from '../../types/link';
import { imageToPicture } from '../../../app/components/plain-image/plain-image.component';
import { alignToCropType } from '../../components/plain-video/plain-video.component';
import { SectionAttributes, Section, InnerSection, InnerSectionAttributes } from '../../../app/types/section';
import { SectionComponent } from '../../components/section/section.component';
import { OgImage, OgImageService } from '../../../app/providers/og-image.service';
import { IMAGE_COMPONENT_NAME } from '../../../app/types/image';
import { HeroItem, HeroPicture, HeroVideo } from '@knorr-bremse-portals/ngx-components/components/hero/hero-item';
import { HeroCarouselComponent, HeroCarouselPage, provideHeroCarouselIntl } from '@knorr-bremse-portals/ngx-components/components/hero/hero-carousel';
import { LocalHeroCarouselIntl } from '../../providers/intl/hero-carousel.intl';
import { FsButtonComponent } from '../../components/fs-button/fs-button.component';
import { ImageOverlayFigureSection, InternalVideoOverlayFigureSection } from '../../components/visual-overlayed-figure/visual-overlayed-figure.component';

export type InternalVisualOverlayFigureSection = ImageOverlayFigureSection | InternalVideoOverlayFigureSection;

interface HeaderMediumSliderPageAttributes extends InnerSectionAttributes {
  mediums: (InternalVisualOverlayFigureSection)[];
  mediumsMobile: (InternalVisualOverlayFigureSection)[];
}

type HeaderMediumSliderPageEditorIds = [
  'numColumns',
  'mediums',
]

export type HeaderMediumSliderPageSection = InnerSection<HeaderMediumSliderPageAttributes, HeaderMediumSliderPageEditorIds, 'header-medium-slider-page'>;

export function mediumSectionToHeroItem(medium: InternalVisualOverlayFigureSection, aspectRatio: AspectRatio): HeroItem {
  if (medium.component === IMAGE_COMPONENT_NAME) {

    const picture = imageToPicture(medium.attributes, aspectRatio);

    return {
      type: 'picture',
      picture: picture.sources,
      alt: picture.alt,
      heading: medium.attributes.topic,
      headingLevel: medium.attributes.headingLevel,
      abstract: medium.attributes.caption,
      link: medium.attributes.link ? toPlainLink(medium.attributes.link) : undefined,
    } as HeroPicture;

  } else {

    return {
      type: 'video',
      src: medium.attributes.src,
      poster: medium.attributes.thumbnailSrc,
      crop: alignToCropType(medium.attributes.align),
      autoplay: medium.attributes.autoplay,
      loop: medium.attributes.loop,
      heading: medium.attributes.caption,
      headingLevel: medium.attributes.headingLevel,
    } as HeroVideo;

  }
}

type HeaderMediumSliderSection = Section<
  {
    numColumns: 4 | 6 | 8 | 12;
    pages: HeaderMediumSliderPageSection[];
  } & SectionAttributes,
  [
    'numColumns',
  ],
  'header-medium-slider'
>;

@Component({
  selector: 'app-header-medium-slider',
  styleUrls: ['./header-medium-slider.component.scss'],
  templateUrl: './header-medium-slider.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    HeroCarouselComponent,
    FsButtonComponent,
],
  providers: [
    provideHeroCarouselIntl(LocalHeroCarouselIntl),
  ],
})
export class HeaderMediumSliderComponent extends SectionComponent<HeaderMediumSliderSection> implements OnDestroy, OnChanges {

  private ogImageService = inject(OgImageService);
  private ogImages: (OgImage | undefined)[] = [];

  protected pages: HeroCarouselPage[] = [];

  public ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges['attributes']) {
      this.pages = this.attributes.pages.map((page): HeroCarouselPage => {
        const itemsOnPage = page.attributes.mediums.length;
        const desktopAspectRatio = itemsOnPage === 1 ? AspectRatio.Wide : AspectRatio.Standard;

        const items = page.attributes.mediums.map((medium): HeroItem => mediumSectionToHeroItem(medium, desktopAspectRatio));

        if (desktopAspectRatio !== AspectRatio.Standard) {
          items.forEach((item, index) => {
            if (item.type === 'picture') {

              const mobileMedium = page.attributes.mediumsMobile[index] as ImageOverlayFigureSection;

              const mobilePicture = imageToPicture({
                src: mobileMedium.attributes.src,
                srcset: mobileMedium.attributes.srcset,
                alt: mobileMedium.attributes.alt,
                original: mobileMedium.attributes.original,
              }, AspectRatio.Standard);

              item.picture[AspectRatio.Standard] = mobilePicture.sources[AspectRatio.Standard];
            }
          })
        }

        if (items.length === 1) {
          return items[0];
        }

        return items;
      });

      this.clearOgImages();

      this.pages.forEach((page) => {
        if (!Array.isArray(page)) {
          page = [page];
        }

        page
          .filter((item): item is HeroPicture => item.type === 'picture')
          .forEach((item) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.ogImages.push(this.ogImageService.add(item.picture.original!['?'], item.alt));
          });
      });
    }
  }

  public ngOnDestroy(): void {
    this.clearOgImages();
  }

  private clearOgImages(): void {
    this.ogImages
      .filter((result): result is OgImage => result !== undefined)
      .forEach((result) =>  this.ogImageService.remove(result));
  }

}
